import { useEffect, useRef, useState } from 'react';
import api from '../utils/api';

// Idle auto-logout, client side.
//
// The server is the authority — it signs a session out once no request has
// arrived inside the window. This hook exists so that (a) typing a long order
// form counts as being active even though it makes no requests, and (b) nobody
// is dumped to the login screen without warning.
//
// While the user is actually interacting we refresh the server's clock at most
// once every few minutes. When they go quiet we show a countdown, then sign out.

const WARN_SECONDS = 120;        // how long the warning shows before sign-out
const PING_EVERY_MS = 4 * 60 * 1000;
const TICK_MS = 15 * 1000;
const ACTIVITY = ['mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

export default function useIdleTimeout(idleMinutes, onTimeout) {
  const [secondsLeft, setSecondsLeft] = useState(null);   // null = not warning
  const lastActivity = useRef(Date.now());
  const lastPing = useRef(Date.now());
  const firedRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!idleMinutes || idleMinutes <= 0) { setSecondsLeft(null); return undefined; }

    const windowMs = idleMinutes * 60 * 1000;
    const markActive = () => {
      lastActivity.current = Date.now();
      // Real interaction keeps the server session alive even with no API calls.
      if (Date.now() - lastPing.current > PING_EVERY_MS) {
        lastPing.current = Date.now();
        api.get('/auth/me').catch(() => {});
      }
    };

    ACTIVITY.forEach(e => window.addEventListener(e, markActive, { passive: true }));

    const tick = setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;
      const msLeft = windowMs - idleFor;

      if (msLeft <= 0) {
        if (!firedRef.current) { firedRef.current = true; onTimeoutRef.current?.(); }
        return;
      }
      setSecondsLeft(msLeft <= WARN_SECONDS * 1000 ? Math.ceil(msLeft / 1000) : null);
    }, TICK_MS);

    return () => {
      ACTIVITY.forEach(e => window.removeEventListener(e, markActive));
      clearInterval(tick);
    };
  }, [idleMinutes]);

  // "Stay signed in" — reset both clocks.
  const staySignedIn = () => {
    lastActivity.current = Date.now();
    lastPing.current = Date.now();
    setSecondsLeft(null);
    api.get('/auth/me').catch(() => {});
  };

  return { secondsLeft, staySignedIn };
}
